import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast } from '../../../../../../ui/toast';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../i18n/constants/keys';
import { getJobOpeningDetails } from '../../../../../../utils/api_service/JobOpenings';
import { updateJobPostUpload } from '../../../../../../utils/api_service/JobPosting';
import { getOrganization } from '../../../../../../utils/api_service/organization';
import { toISODateString } from '../../../../../../utils/helpers/dateUtils';
import { RootState } from '../../../../../../utils/redux_store/store';
import { IJobPostingTabs } from '../Interfaces/IJobPostingTabs';

const isLicenseActive = (feature?: { startDate: number | string; endDate: number | string }): boolean => {
  if (!feature) return false;
  return Number(feature.endDate) * 1000 > Date.now();
};

export const useJobPostingTabs = (props: IJobPostingTabs) => {
  const {
    jobPostingData,
    setJobPostingData,
    jobTitleList,
    jobDescription,
    jobPostExist,
    setStages,
  } = props;
  const { t } = useTranslation(NS.adminPages);
  const { subscriptions } = useSelector((state: RootState) => state.subscriptions);
  const hasCCALicense = isLicenseActive(subscriptions?.hierTalentCca);

  const [activeTab, setActiveTab] = useState('jobPostConfig');
  const tabTitles = [
    { label: t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.tabs.config), value: 'jobPostConfig' },
    { label: t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.tabs.details), value: 'jobPostDetails' },
  ];
  const [jobPostId, setJobPostId] = useState<string>('');
  const [orgPerksAndBenefits, setOrgPerksAndBenefits] = useState<string>('');
  const [orgCompanyDetails, setOrgCompanyDetails] = useState<string>('');
  const { id } = useParams<{ id: string }>();

  const handleToggle = async () => {
    const updatedActive = !jobPostingData.active;
    const updatedJobPostingData = {
      ...jobPostingData,
      active: updatedActive,
    };

    setJobPostingData(updatedJobPostingData);

    try {
      if (jobPostId !== '') {
        const updateEmailResponse = await updateJobPostUpload(
          jobPostId,
          id,
          updatedJobPostingData.department,
          updatedJobPostingData.jobTitle,
          updatedJobPostingData.workType,
          updatedJobPostingData.jsonTemplate || '',
          updatedJobPostingData.htmlTemplate || '',
          toISODateString(updatedJobPostingData.startDate),
          toISODateString(updatedJobPostingData.endDate),
          updatedJobPostingData.active,
          updatedJobPostingData.location,
          updatedJobPostingData.videoFilename instanceof File
            ? updatedJobPostingData.videoFilename
            : undefined,
          updatedJobPostingData.videoDescription
            ? updatedJobPostingData.videoDescription
            : undefined,
          updatedJobPostingData.videoTitle
            ? updatedJobPostingData.videoTitle
            : undefined,
        );

        if (updateEmailResponse.ok) {
          toast.success(t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.toasts.jobPostUpdated), { position: 'bottom-center' });
        } else {
          throw new Error(t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.toasts.errorUpdating));
        }
      } else {
        setJobPostingData({
          ...jobPostingData,
          active: !jobPostingData.active,
        });
      }
    } catch (error) {
      toast.error((error as Error).message, { position: 'bottom-center' });
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch Job Opening Details
        const jobResponse = await getJobOpeningDetails(id);
        if (!jobResponse.ok) {
          throw new Error('Error Fetching Job Requisition');
        }
        const originalData = await jobResponse.json();

        if (originalData.jobPosting) {
          setJobPostId(originalData.jobPosting.id);
        }
        if (jobPostingData.jobTitle === '') {
          setJobPostingData({
            ...jobPostingData,
            jobTitle: originalData.jobDescription.jobTitle.jobTitle,
            location: originalData.jobDescription.jobLocation,
          });
        }

        // Fetch Organization Details
        const orgResponse = await getOrganization();
        if (orgResponse.ok) {
          const organization = await orgResponse.json();
          setOrgCompanyDetails(organization.companyDetails);
          setOrgPerksAndBenefits(organization.perksAndBenefits);
        }
      } catch (error: any) {
        if (error.message === 'Unexpected error') {
          toast.error(t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.toasts.errorLoadingRequisition), {
            position: 'bottom-center',
          });
        } else {
          console.error(error);
        }
      } finally {
        console.log(false);
      }
    };

    fetchData();
  }, []);



  return {
    activeTab,
    tabTitles,
    jobPostId,
    orgPerksAndBenefits,
    orgCompanyDetails,
    handleToggle,
    setActiveTab,
    setJobPostId,
    hasCCALicense,
  };
};
